'use client';

import { Box, Typography } from '@mui/material';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { BRAND_LUXE_PRIMARY, BRAND_SURFACE_CONTAINER_LOW } from '../brandDashboardTokens';
import type { ICampaignWorkspaceData } from '../../../redux/buzzbridge/campaignWorkspace.types';

type CircuulStats = NonNullable<ICampaignWorkspaceData['circuul_stats']>;

function fmt(cents: number): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

interface StatPillProps {
    label: string;
    value: string | number;
    icon: React.ReactNode;
    accent?: string;
}

function StatPill({ label, value, icon, accent = '#fff' }: StatPillProps) {
    return (
        <Box
            sx={{
                flex: '1 1 0',
                minWidth: 100,
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 2,
                p: { xs: 1.5, sm: 2 },
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'rgba(255,255,255,0.5)' }}>
                {icon}
                <Typography sx={{ fontSize: 10, fontFamily: FONT_ENUM.MAIN_BOLD, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
                    {label}
                </Typography>
            </Box>
            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: { xs: 20, sm: 24 }, color: accent, lineHeight: 1.1 }}>
                {value}
            </Typography>
        </Box>
    );
}

interface CircuulCreatorStatsCardProps {
    stats: CircuulStats;
}

export function CircuulCreatorStatsCard({ stats }: CircuulCreatorStatsCardProps) {
    const isWebVisit = stats.kind === 'web_visit';
    const KindIcon = isWebVisit ? LanguageOutlinedIcon : PhoneAndroidOutlinedIcon;
    const kindLabel = isWebVisit ? 'Web Visit Campaign' : 'App Install Campaign';

    return (
        <Box
            sx={{
                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                border: `1px solid ${BRAND_LUXE_PRIMARY}30`,
                borderRadius: 2,
                p: { xs: 2, sm: 3 },
            }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <KindIcon sx={{ fontSize: 16, color: BRAND_LUXE_PRIMARY }} />
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: BRAND_LUXE_PRIMARY, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    {kindLabel} · Performance
                </Typography>
                <Box
                    sx={{
                        ml: 'auto',
                        px: 1.25,
                        py: 0.35,
                        borderRadius: 999,
                        bgcolor: 'rgba(255,255,255,0.06)',
                        fontSize: 10,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        color: 'rgba(255,255,255,0.5)',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                    }}>
                    Code {stats.code}
                </Box>
            </Box>

            {/* Stat pills */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <StatPill
                    label="Clicks"
                    value={stats.clicks}
                    icon={<TouchAppOutlinedIcon sx={{ fontSize: 13 }} />}
                />
                <StatPill
                    label={stats.conversion_label}
                    value={stats.conversions}
                    icon={<CheckCircleOutlineIcon sx={{ fontSize: 13 }} />}
                    accent="#00fdc1"
                />
                <StatPill
                    label={`Rate`}
                    value={stats.clicks > 0 ? `${((stats.conversions / stats.clicks) * 100).toFixed(1)}%` : '—'}
                    icon={<CheckCircleOutlineIcon sx={{ fontSize: 13 }} />}
                    accent="#ffc965"
                />
                <StatPill
                    label="CPA"
                    value={fmt(stats.cpa_cents)}
                    icon={<CheckCircleOutlineIcon sx={{ fontSize: 13 }} />}
                />
                <StatPill
                    label="Earned"
                    value={fmt(stats.total_earned_cents)}
                    icon={<CheckCircleOutlineIcon sx={{ fontSize: 13 }} />}
                    accent="#ffc965"
                />
            </Box>

            {/* Pending / cleared breakdown */}
            {(stats.pending_cents > 0 || stats.cleared_cents > 0) && (
                <Typography sx={{ mt: 1.5, fontSize: 11, fontFamily: FONT_ENUM.MAIN_MEDIUM, color: 'rgba(255,255,255,0.4)' }}>
                    {fmt(stats.pending_cents)} pending · {fmt(stats.cleared_cents)} cleared
                </Typography>
            )}
        </Box>
    );
}
